"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableGeoPushpinChart = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var Visualization_1 = require("../../../interfaces/Visualization");
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var bucket_1 = require("../../../constants/bucket");
var uiConfig_1 = require("../../../constants/uiConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var geoPushpinChartUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/geoPushpinChartUiConfigHelper");
var properties_1 = require("../../../constants/properties");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var GeoPushpinConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/GeoPushpinConfigurationPanel"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_geo_1 = require("@gooddata/sdk-ui-geo");
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var includes_1 = tslib_1.__importDefault(require("lodash/includes"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var bucketConfig_1 = require("../../../utils/bucketConfig");
var NUMBER_MEASURES_IN_BUCKETS_LIMIT = 2;
var PluggableGeoPushpinChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableGeoPushpinChart, _super);
    function PluggableGeoPushpinChart(props) {
        var _this = _super.call(this, props) || this;
        // This is effectively calling super.handlePushData()
        // https://stackoverflow.com/questions/31088947/inheritance-method-call-triggers-typescript-compiler-error
        // https://github.com/basarat/typescript-book/blob/master/docs/arrow-functions.md#tip-arrow-functions-and-inheritance
        _this.superHandlePushData = _this.handlePushData;
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        _this.handlePushData = function (data) {
            // For pushpin chart we do not support drilling from attributes.
            _this.superHandlePushData(tslib_1.__assign(tslib_1.__assign({}, data), (data.availableDrillTargets && {
                availableDrillTargets: _this.withEmptyAttributeTargets(data),
            })));
        };
        var element = props.element, visualizationProperties = props.visualizationProperties;
        _this.type = sdk_ui_1.VisualizationTypes.PUSHPIN;
        _this.geoPushpinElement = element;
        _this.initializeProperties(visualizationProperties);
        return _this;
    }
    PluggableGeoPushpinChart.prototype.checkBeforeRender = function (insight) {
        if (!sdk_model_1.insightHasDataDefined(insight)) {
            throw new Visualization_1.EmptyAfmSdkError();
        }
        return true;
    };
    PluggableGeoPushpinChart.prototype.getExtendedReferencePoint = function (referencePoint) {
        var _this = this;
        return _super.prototype.getExtendedReferencePoint.call(this, referencePoint)
            .then(function (extendedReferencePoint) {
            var newReferencePoint = geoPushpinChartUiConfigHelper_1.setGeoPushpinUiConfig(extendedReferencePoint, _this.intl, _this.type);
            newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, true);
            return _this.updateSupportedProperties(newReferencePoint);
        });
    };
    PluggableGeoPushpinChart.prototype.getUiConfig = function () {
        return cloneDeep_1.default(uiConfig_1.GEO_PUSHPIN_CHART_UICONFIG);
    };
    PluggableGeoPushpinChart.prototype.getExecution = function (options, insight, executionFactory) {
        var _a;
        var executionConfig = options.executionConfig;
        var buckets = this.prepareBuckets(insight);
        return (_a = executionFactory
            .forBuckets(buckets, sdk_model_1.insightFilters(insight))
            .withDimensions(sdk_ui_geo_1.getGeoChartDimensions))
            .withSorting.apply(_a, this.createSort(insight)).withExecConfig(executionConfig);
    };
    PluggableGeoPushpinChart.prototype.getSupportedPropertiesList = function () {
        return supportedProperties_1.GEOPUSHPIN_SUPPORTED_PROPERTIES;
    };
    PluggableGeoPushpinChart.prototype.configureBuckets = function (extendedReferencePoint) {
        var newExtendedReferencePoint = this.sanitizeMeasures(extendedReferencePoint);
        var buckets = bucketHelper_1.limitNumberOfMeasuresInBuckets(newExtendedReferencePoint.buckets, NUMBER_MEASURES_IN_BUCKETS_LIMIT);
        var allMeasures = bucketHelper_1.getAllMeasures(buckets);
        var primaryMeasures = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.MEASURES, sdk_ui_1.BucketNames.SIZE], [bucket_1.METRIC]);
        var secondaryMeasures = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.SECONDARY_MEASURES, sdk_ui_1.BucketNames.COLOR], [bucket_1.METRIC]);
        var sizeMeasures = (primaryMeasures.length > 0
            ? primaryMeasures
            : allMeasures.filter(function (measure) { return !includes_1.default(secondaryMeasures, measure); })).slice(0, this.getPreferredBucketItemLimit(sdk_ui_1.BucketNames.SIZE));
        var colorMeasures = (secondaryMeasures.length > 0
            ? secondaryMeasures
            : allMeasures.filter(function (measure) { return !includes_1.default(sizeMeasures, measure); })).slice(0, this.getPreferredBucketItemLimit(sdk_ui_1.BucketNames.COLOR));
        set_1.default(newExtendedReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.LOCATION,
                items: this.getLocationItems(buckets),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SIZE,
                items: bucketHelper_1.removeShowOnSecondaryAxis(sizeMeasures),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.COLOR,
                items: bucketHelper_1.removeShowOnSecondaryAxis(colorMeasures),
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SEGMENT,
                items: this.getSegmentItems(buckets),
            },
        ]);
        return newExtendedReferencePoint;
    };
    PluggableGeoPushpinChart.prototype.renderConfigurationPanel = function (insight) {
        var configPanelElement = document.querySelector(this.configPanelElement);
        // NOTE: using pushData directly; no handlePushData here as in other visualizations.
        if (configPanelElement) {
            this.renderFun(react_1.default.createElement(GeoPushpinConfigurationPanel_1.default, { locale: this.locale, pushData: this.pushData, properties: this.visualizationProperties, references: this.references, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags }), configPanelElement);
        }
    };
    PluggableGeoPushpinChart.prototype.buildVisualizationConfig = function (options, supportedControls) {
        var _a = options.config, config = _a === void 0 ? {} : _a, _b = options.customVisualizationConfig, customVisualizationConfig = _b === void 0 ? {} : _b;
        var center = supportedControls.center, legend = supportedControls.legend, _c = supportedControls.viewport, viewport = _c === void 0 ? {} : _c;
        var colorMapping = _super.prototype.buildVisualizationConfig.call(this, options, supportedControls).colorMapping;
        var centerProp = center ? { center: center } : {};
        var legendProp = legend ? { legend: legend } : {};
        var isInEditMode = config.isInEditMode, isExportMode = config.isExportMode;
        if (this.environment === properties_1.DASHBOARDS_ENVIRONMENT && this.featureFlags["enableKDWidgetCustomHeight"]) {
            set_1.default(supportedControls, "legend.responsive", "autoPositionWithPopup");
        }
        var viewportProp = {
            viewport: tslib_1.__assign(tslib_1.__assign({}, viewport), { frozen: isInEditMode || isExportMode }),
        };
        var geoChartConfig = tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, config), centerProp), legendProp), viewportProp);
        return tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({ separators: config.separators, colorPalette: config.colorPalette, mapboxToken: config.mapboxToken }, supportedControls), geoChartConfig), { colorMapping: colorMapping }), customVisualizationConfig);
    };
    PluggableGeoPushpinChart.prototype.renderVisualization = function (options, insight, executionFactory) {
        var _a = options.dimensions, dimensions = _a === void 0 ? { height: undefined } : _a, _b = options.custom, custom = _b === void 0 ? {} : _b, locale = options.locale, theme = options.theme;
        var height = dimensions.height;
        var _c = this, geoPushpinElement = _c.geoPushpinElement, intl = _c.intl;
        // keep height undef for AD; causes indigo-visualizations to pick default 100%
        var resultingHeight = this.environment === properties_1.DASHBOARDS_ENVIRONMENT ? height : undefined;
        var drillableItems = custom.drillableItems;
        var supportedControls = this.visualizationProperties.controls || {};
        var fullConfig = this.buildVisualizationConfig(options, supportedControls);
        var execution = this.getExecution(options, insight, executionFactory);
        var geoPushpinProps = {
            drillableItems: drillableItems,
            config: fullConfig,
            height: resultingHeight,
            intl: intl,
            locale: locale,
            execution: execution,
            pushData: this.handlePushData,
            afterRender: this.afterRender,
            onDrill: this.onDrill,
            onError: this.onError,
            onExportReady: this.onExportReady,
            onLoadingChanged: this.onLoadingChanged,
            LoadingComponent: null,
            ErrorComponent: null,
            theme: theme,
        };
        this.renderFun(react_1.default.createElement(sdk_ui_geo_1.CoreGeoChart, tslib_1.__assign({}, geoPushpinProps)), document.querySelector(geoPushpinElement));
    };
    PluggableGeoPushpinChart.prototype.withEmptyAttributeTargets = function (data) {
        return tslib_1.__assign(tslib_1.__assign({}, data.availableDrillTargets), { attributes: [] });
    };
    PluggableGeoPushpinChart.prototype.sanitizeMeasures = function (extendedReferencePoint) {
        var newExtendedReferencePoint = bucketHelper_1.removeAllArithmeticMeasuresFromDerived(extendedReferencePoint);
        return bucketHelper_1.removeAllDerivedMeasures(newExtendedReferencePoint);
    };
    PluggableGeoPushpinChart.prototype.createSort = function (insight) {
        var bucket = sdk_model_1.insightBucket(insight, sdk_ui_1.BucketNames.SEGMENT);
        var segmentAttribute = bucket && sdk_model_1.bucketAttribute(bucket);
        // sort by second attribute (1st: location, 2nd: segmentBy, 3rd: tooltipText)
        if (segmentAttribute) {
            return [sdk_model_1.newAttributeSort(segmentAttribute, "asc")];
        }
        return [];
    };
    PluggableGeoPushpinChart.prototype.getSegmentItems = function (buckets) {
        var segments = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.STACK, sdk_ui_1.BucketNames.SEGMENT, sdk_ui_1.BucketNames.COLUMNS], [bucket_1.ATTRIBUTE]);
        var nonSegmentAttributes = bucketHelper_1.getAttributeItemsWithoutStacks(buckets);
        if (nonSegmentAttributes.length > 1 && isEmpty_1.default(segments)) {
            var locationItems_1 = this.getLocationItems(buckets);
            segments = nonSegmentAttributes
                .filter(function (attribute) { return !includes_1.default(locationItems_1, attribute); })
                .filter(function (attribute) { return !bucketHelper_1.isDateBucketItem(attribute); })
                .slice(0, 1);
        }
        return segments.slice(0, this.getPreferredBucketItemLimit(sdk_ui_1.BucketNames.SEGMENT));
    };
    PluggableGeoPushpinChart.prototype.getLocationItems = function (buckets) {
        var locationItems = bucketHelper_1.getItemsFromBuckets(buckets, [sdk_ui_1.BucketNames.ATTRIBUTE, sdk_ui_1.BucketNames.VIEW, sdk_ui_1.BucketNames.LOCATION, sdk_ui_1.BucketNames.TREND], [bucket_1.ATTRIBUTE]).filter(function (bucketItem) { return Boolean(bucketItem.locationDisplayFormRef); });
        return locationItems.slice(0, this.getPreferredBucketItemLimit(sdk_ui_1.BucketNames.LOCATION));
    };
    PluggableGeoPushpinChart.prototype.getPreferredBucketItemLimit = function (preferredBucket) {
        var bucketsUiConfig = this.getUiConfig().buckets;
        return bucketsUiConfig[preferredBucket].itemsLimit;
    };
    PluggableGeoPushpinChart.prototype.updateSupportedProperties = function (referencePoint) {
        var _a, _b, _c;
        var buckets = (_a = referencePoint === null || referencePoint === void 0 ? void 0 : referencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var locationItem = this.getLocationItems(buckets)[0];
        if (!locationItem) {
            return referencePoint;
        }
        var referencePointConfigured = cloneDeep_1.default(referencePoint);
        var dfRef = locationItem.dfRef;
        var visualizationProperties = this.visualizationProperties || {};
        var _d = visualizationProperties.controls, controls = _d === void 0 ? {} : _d;
        var hasSizeMesure = bucketHelper_1.getItemsCount(buckets, sdk_ui_1.BucketNames.SIZE) > 0;
        var hasColorMesure = bucketHelper_1.getItemsCount(buckets, sdk_ui_1.BucketNames.COLOR) > 0;
        var hasLocationAttribute = bucketHelper_1.getItemsCount(buckets, sdk_ui_1.BucketNames.LOCATION) > 0;
        var hasSegmentAttribute = bucketHelper_1.getItemsCount(buckets, sdk_ui_1.BucketNames.SEGMENT) > 0;
        var groupNearbyPoints = hasLocationAttribute && !hasColorMesure && !hasSizeMesure && !hasSegmentAttribute;
        // for tooltip, prefer standard text display form (whose type is `undefined`) over geo or hyperlink display forms
        var tooltipDfRef = ((_c = (_b = locationItem.displayForms) === null || _b === void 0 ? void 0 : _b.find(function (displayForm) { return !displayForm.type; })) === null || _c === void 0 ? void 0 : _c.ref) || dfRef;
        var tooltipText = sdk_model_1.isUriRef(tooltipDfRef) ? tooltipDfRef.uri : tooltipDfRef.identifier;
        set_1.default(referencePointConfigured, "properties", {
            controls: tslib_1.__assign(tslib_1.__assign({ points: {
                    groupNearbyPoints: groupNearbyPoints,
                } }, controls), { tooltipText: tooltipText }),
        });
        if (this.references) {
            set_1.default(referencePointConfigured, "references", this.references);
        }
        return referencePointConfigured;
    };
    PluggableGeoPushpinChart.prototype.prepareBuckets = function (insight) {
        var _a;
        var supportedControls = ((_a = this.visualizationProperties) === null || _a === void 0 ? void 0 : _a.controls) || {};
        // we need to shallow copy the buckets so that we can add more without mutating the original array
        var buckets = tslib_1.__spreadArrays(sdk_model_1.insightBuckets(insight));
        if (supportedControls && (supportedControls === null || supportedControls === void 0 ? void 0 : supportedControls.tooltipText)) {
            var tooltipText = supportedControls === null || supportedControls === void 0 ? void 0 : supportedControls.tooltipText;
            /*
             * The display form to use for tooltip text is provided in properties :( This is unfortunate; the chart
             * props could very well contain an extra prop for the tooltip bucket.
             *
             * Current guess is that this is because AD creates insight buckets; in order to create the tooltip
             * bucket, AD would have to actually show the tooltip bucket in the UI - which is not desired. Thus the
             * displayForm to add as bucket is passed in visualization properties.
             *
             * This workaround is highly unfortunate for two reasons:
             *
             * 1.  It leaks all the way to the API of geo chart: bucket geo does not have the tooltip bucket. Instead
             *     it duplicates then here logic in chart transform
             *
             * 2.  The executeVisualization endpoint is useless for GeoChart; cannot be used to render geo chart because
             *     the buckets stored in vis object are not complete. execVisualization takes buckets as is.
             */
            var locationBucket = sdk_model_1.insightBucket(insight, sdk_ui_1.BucketNames.LOCATION);
            var ref = sdk_model_1.idRef(tooltipText, "displayForm");
            var alias_1 = "";
            if (locationBucket) {
                var attribute = sdk_model_1.bucketAttribute(locationBucket);
                if (attribute) {
                    alias_1 = sdk_model_1.attributeAlias(attribute);
                    if (sdk_model_1.isUriRef(sdk_model_1.attributeDisplayFormRef(attribute))) {
                        ref = sdk_model_1.uriRef(tooltipText);
                    }
                }
            }
            var existingTooltipTextBucket = sdk_model_1.insightBucket(insight, sdk_ui_1.BucketNames.TOOLTIP_TEXT);
            if (!existingTooltipTextBucket) {
                buckets.push(sdk_model_1.newBucket(sdk_ui_1.BucketNames.TOOLTIP_TEXT, sdk_model_1.newAttribute(ref, function (m) { return m.localId("tooltipText_df").alias(alias_1); })));
            }
        }
        return buckets;
    };
    return PluggableGeoPushpinChart;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableGeoPushpinChart = PluggableGeoPushpinChart;
//# sourceMappingURL=PluggableGeoPushpinChart.js.map